import { ChangeDetectionStrategy, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { buildProviders, Coerce, KalDialogConfig, KalDialogService } from '@kalidea/kaligraphi';
import {
    ConfirmDialogComponent,
    ConfirmDialogData
} from 'src/app/_shared/dialogs/confirm-dialog/confirm-dialog.component';
import { filter, tap } from 'rxjs/operators';
import get from 'lodash-es/get';
import { CustomFormControlAccess } from 'src/app/_core/tools/custom-form-control-access';
import { Observable, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { arrayNotEmpty } from 'src/app/utils/array.utils';
import { isNullOrEmpty } from 'src/app/utils/string.utils';

export enum CommentScope {
    Unscoped,
    Both,
}

enum CommentType {
    Public = 'public',
    Private = 'prive'
}

@Component({
    selector: 'app-shared-comment-edit',
    templateUrl: './shared-comment-edit.component.html',
    styleUrls: ['./shared-comment-edit.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: buildProviders(SharedCommentEditComponent),
    standalone: false
})
export class SharedCommentEditComponent extends CustomFormControlAccess implements OnInit {

    form: UntypedFormGroup;

    isPublicCommentDisplayed = false;
    isPrivateCommentDisplayed = false;

    @Coerce('boolean')
    @Input() disabledPublicComment = false;

    @Coerce('boolean')
    @Input() disabledPrivateComment = false;

    @Coerce('boolean')
    @Input() isOpened = false;

    @Input() publicCommentValidators: Validators[] = [];
    @Input() privateCommentValidators: Validators[] = [];

    CommentType = CommentType;

    constructor(private dialogService: KalDialogService,
                private fb: UntypedFormBuilder) {
        super();
    }

    private _publicCommentLabel = 'Commentaire public';

    get publicCommentLabel(): string {
        return this._publicCommentLabel;
    }

    @Input()
    set publicCommentLabel(label: string) {
        this._publicCommentLabel = isNullOrEmpty(label) ? 'Commentaire public' : label;
    }

    private _privateCommentLabel = 'Commentaire privé';

    get privateCommentLabel(): string {
        return this._privateCommentLabel;
    }

    @Input()
    set privateCommentLabel(label: string) {
        this._privateCommentLabel = isNullOrEmpty(label) ? 'Commentaire privé' : label;
    }

    get areCommentsDisplayed(): boolean {
        return this.isPublicCommentDisplayed || this.isPrivateCommentDisplayed;
    }

    get commentQuantity(): number {
        return [this.disabledPublicComment, this.disabledPrivateComment].filter(e => !e).length;
    }

    writeValue(value: any): void {
        value = value || {publicComment: null, privateComment: null};
        const privateComment = get(value, 'privateComment', false);
        const publicComment = get(value, 'publicComment', false);
        const isOpened = this.isOpened && !privateComment && !publicComment;

        super.writeValue(value);
        this.form.patchValue(value);

        if (privateComment || isOpened) {
            this.isPrivateCommentDisplayed = true;
        }

        if (publicComment || isOpened) {
            this.isPublicCommentDisplayed = true;
        }
    }

    displayAllComments(): void {
        if (!this.disabledPublicComment) {
            this.displayComment(CommentType.Public);
        }
        if (!this.disabledPrivateComment) {
            this.displayComment(CommentType.Private);
        }
    }

    displayComment(commentType: CommentType): void {
        if (commentType === CommentType.Public) {
            this.isPublicCommentDisplayed = true;
        } else {
            this.isPrivateCommentDisplayed = true;
        }
    }

    clearComment(controlName): void {
        this.form.get(controlName).patchValue('');
    }

    openConfirmClearDialog(controlName: string): void {
        if (!this.form.value[controlName]) {
            this.clearComment(controlName);
            return;
        }
        const commentLabel = controlName === 'publicComment' ? this.publicCommentLabel : this.privateCommentLabel;

        const config = new KalDialogConfig<ConfirmDialogData>({
            title: 'Effacer le commentaire',
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-dark-backdrop',
            data: {
                body: `Souhaitez-vous effacer le ${commentLabel.toLowerCase()} ?`,
                submitLabel: 'Effacer',
                cancelLabel: 'Annuler'
            }
        });
        const dialogRef = this.dialogService.open(ConfirmDialogComponent, config);

        dialogRef.afterClosed.pipe(
            filter((result: ConfirmDialogData) => !!result && result.accepted),
            tap(() => this.clearComment(controlName))
        ).subscribe();
    }

    validate(c: AbstractControl): Observable<{}> {
        return of(this.form.valid ? null : {commenterror: true});
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            publicComment: ['', arrayNotEmpty(this.publicCommentValidators) ? this.publicCommentValidators : [Validators.maxLength(environment.constants.maxLengthComment)]],
            privateComment: ['', arrayNotEmpty(this.privateCommentValidators) ? this.privateCommentValidators : [Validators.maxLength(environment.constants.maxLengthComment)]]
        });

        this.watch$ = this.form.valueChanges.pipe(
            tap(() => {
                super.notifyUpdate(this.form.value);
            })
        );
    }
}
